/**
 * Treatment Protocol Selector
 * 
 * Modal for selecting predefined treatment protocols based on AI recommendations.
 * Selecting a protocol can auto-populate prescriptions, labs, and follow-up schedules.
 */

import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  ClipboardCheck,
  Search,
  CheckCircle2,
  Pill,
  FlaskConical,
  Calendar,
  ChevronRight,
  Sparkles,
  Loader2,
  ArrowRight,
} from 'lucide-react';
import { ClinicalAction } from '@/lib/intellihealthActions';
import { FHIRPatient } from '@/services/fhirPatientsService';
import { toast } from '@/hooks/use-toast';

interface TreatmentProtocolSelectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  action: ClinicalAction | null;
  patient: FHIRPatient | null;
  onSelectProtocol?: (protocol: TreatmentProtocol) => Promise<void>;
}

export interface TreatmentProtocol {
  id: string;
  name: string;
  description: string;
  category: string;
  duration: string;
  medications: Array<{
    name: string;
    dose: string;
    frequency: string;
  }>;
  labs: string[];
  followUpWeeks: number;
  contraindications?: string[];
  tags: string[];
}

// Sample treatment protocols
const TREATMENT_PROTOCOLS: TreatmentProtocol[] = [
  {
    id: 'menopause-hrt-standard',
    name: 'Standard Menopause HRT',
    description: 'Bioidentical hormone replacement therapy for menopausal symptoms',
    category: 'Menopause',
    duration: '12 weeks initial',
    medications: [
      { name: 'Estradiol', dose: '0.5mg', frequency: 'Daily' },
      { name: 'Progesterone', dose: '100mg', frequency: 'Nightly' },
    ],
    labs: ['Estradiol', 'Progesterone', 'FSH', 'LH'],
    followUpWeeks: 8,
    contraindications: ['History of breast cancer', 'Active thrombosis'],
    tags: ['HRT', 'Bioidentical', 'Menopause'],
  },
  {
    id: 'menopause-hrt-plus',
    name: 'Enhanced Menopause Protocol',
    description: 'Comprehensive HRT with testosterone and DHEA for optimal results',
    category: 'Menopause',
    duration: '12 weeks initial',
    medications: [
      { name: 'Estradiol', dose: '1mg', frequency: 'Daily' },
      { name: 'Progesterone', dose: '200mg', frequency: 'Nightly' },
      { name: 'Testosterone Cream', dose: '0.5mg', frequency: 'Daily' },
      { name: 'DHEA', dose: '25mg', frequency: 'Daily' },
    ],
    labs: ['Complete Hormone Panel', 'Metabolic Panel', 'Lipid Panel'],
    followUpWeeks: 6,
    tags: ['HRT', 'Comprehensive', 'Testosterone'],
  },
  {
    id: 'pcos-metabolic',
    name: 'PCOS Metabolic Protocol',
    description: 'Metabolic optimization for PCOS with insulin sensitization',
    category: 'PCOS',
    duration: '16 weeks',
    medications: [
      { name: 'Metformin', dose: '500mg', frequency: 'Twice daily' },
      { name: 'Inositol', dose: '2g', frequency: 'Twice daily' },
      { name: 'Vitamin D3', dose: '5000 IU', frequency: 'Daily' },
    ],
    labs: ['Fasting Insulin', 'HbA1c', 'HOMA-IR', 'Testosterone', 'DHEA-S'],
    followUpWeeks: 8,
    tags: ['PCOS', 'Metabolic', 'Insulin Resistance'],
  },
  {
    id: 'thyroid-optimization',
    name: 'Thyroid Optimization Protocol',
    description: 'Comprehensive thyroid support and optimization',
    category: 'Thyroid',
    duration: '8 weeks',
    medications: [
      { name: 'Levothyroxine', dose: '50mcg', frequency: 'Morning, fasting' },
      { name: 'Selenium', dose: '200mcg', frequency: 'Daily' },
      { name: 'Zinc', dose: '30mg', frequency: 'Daily' },
    ],
    labs: ['TSH', 'Free T3', 'Free T4', 'Thyroid Antibodies'],
    followUpWeeks: 6,
    tags: ['Thyroid', 'Hypothyroidism', 'Optimization'],
  },
  {
    id: 'weight-management-glp1',
    name: 'GLP-1 Weight Management',
    description: 'Medical weight management with GLP-1 agonist therapy',
    category: 'Weight Management',
    duration: '24 weeks',
    medications: [
      { name: 'Semaglutide', dose: '0.25mg', frequency: 'Weekly, titrate up' },
      { name: 'Vitamin B12', dose: '1000mcg', frequency: 'Daily' },
    ],
    labs: ['Metabolic Panel', 'Lipid Panel', 'HbA1c', 'Thyroid Panel'],
    followUpWeeks: 4,
    contraindications: ['Personal/family history of MTC', 'MEN 2 syndrome'],
    tags: ['Weight Loss', 'GLP-1', 'Obesity'],
  },
  {
    id: 'perimenopause-support',
    name: 'Perimenopause Support',
    description: 'Gentle hormone support for perimenopausal transition',
    category: 'Perimenopause',
    duration: '12 weeks',
    medications: [
      { name: 'Progesterone', dose: '100mg', frequency: 'Days 14-28' },
      { name: 'Vitamin D3', dose: '2000 IU', frequency: 'Daily' },
      { name: 'Magnesium Glycinate', dose: '400mg', frequency: 'Nightly' },
    ],
    labs: ['Estradiol', 'Progesterone', 'FSH', 'AMH'],
    followUpWeeks: 8,
    tags: ['Perimenopause', 'Progesterone', 'Transition'],
  },
];

const TreatmentProtocolSelector: React.FC<TreatmentProtocolSelectorProps> = ({
  open,
  onOpenChange,
  action,
  patient,
  onSelectProtocol,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProtocol, setSelectedProtocol] = useState<TreatmentProtocol | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Filter protocols based on search and AI recommendation context
  const filteredProtocols = useMemo(() => {
    let protocols = TREATMENT_PROTOCOLS;

    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      protocols = protocols.filter(
        (p) =>
          p.name.toLowerCase().includes(search) ||
          p.description.toLowerCase().includes(search) ||
          p.category.toLowerCase().includes(search) ||
          p.tags.some((t) => t.toLowerCase().includes(search))
      );
    }

    return protocols;
  }, [searchTerm]);

  // Group protocols by category
  const protocolsByCategory = useMemo(() => {
    const grouped: Record<string, TreatmentProtocol[]> = {};
    for (const protocol of filteredProtocols) {
      if (!grouped[protocol.category]) {
        grouped[protocol.category] = [];
      }
      grouped[protocol.category].push(protocol);
    }
    return grouped;
  }, [filteredProtocols]);

  const handleSelectProtocol = async () => {
    if (!selectedProtocol) return;

    setIsSubmitting(true);
    try {
      if (onSelectProtocol) {
        await onSelectProtocol(selectedProtocol);
      }

      toast({
        title: 'Protocol Selected',
        description: `${selectedProtocol.name} has been applied`,
      });

      onOpenChange(false);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to apply protocol'
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden bg-[hsl(var(--ih-bg))] border-[hsl(var(--ih-border))] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-[hsl(var(--ih-text))] flex items-center gap-2">
            <div className="w-8 h-8 bg-brand-teal/20 rounded-lg flex items-center justify-center">
              <ClipboardCheck className="h-4 w-4 text-brand-teal" />
            </div>
            Select Treatment Protocol
          </DialogTitle>
          <DialogDescription className="text-[hsl(var(--ih-text-muted))]">
            {patient && (
              <span>
                For <span className="text-[hsl(var(--ih-text))] font-medium">{patient.first_name} {patient.last_name}</span>
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--ih-text-muted))]" />
          <Input
            placeholder="Search protocols..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-[hsl(var(--ih-bg-elevated))] border-[hsl(var(--ih-border))] text-[hsl(var(--ih-text))]"
          />
        </div>

        {/* Protocols List */}
        <ScrollArea className="flex-1 -mx-6 px-6">
          <div className="space-y-6 pb-4">
            {Object.entries(protocolsByCategory).map(([category, protocols]) => (
              <div key={category}>
                <h3 className="text-xs font-semibold text-[hsl(var(--ih-text-muted))] uppercase tracking-wider mb-3">
                  {category}
                </h3>
                <div className="space-y-2">
                  {protocols.map((protocol) => (
                    <div
                      key={protocol.id}
                      onClick={() => setSelectedProtocol(protocol)}
                      className={`p-4 rounded-lg border cursor-pointer transition-all ${
                        selectedProtocol?.id === protocol.id
                          ? 'bg-brand-teal/10 border-brand-teal/30'
                          : 'bg-[hsl(var(--ih-bg-elevated))] border-[hsl(var(--ih-border))]/50 hover:border-[hsl(var(--ih-border))]'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="text-[hsl(var(--ih-text))] font-medium text-sm">{protocol.name}</h4>
                            {selectedProtocol?.id === protocol.id && (
                              <CheckCircle2 className="h-4 w-4 text-brand-teal" />
                            )}
                          </div>
                          <p className="text-[hsl(var(--ih-text-muted))] text-xs mb-3">{protocol.description}</p>

                          {/* Protocol Details */}
                          <div className="flex flex-wrap gap-2 mb-2">
                            <Badge variant="outline" className="text-[10px] border-[hsl(var(--ih-border))] text-[hsl(var(--ih-text-muted))]">
                              {protocol.duration}
                            </Badge>
                            <Badge variant="outline" className="text-[10px] border-emerald-500/30 text-emerald-400">
                              <Pill className="h-3 w-3 mr-1" />
                              {protocol.medications.length} medications
                            </Badge>
                            <Badge variant="outline" className="text-[10px] border-purple-500/30 text-purple-400">
                              <FlaskConical className="h-3 w-3 mr-1" />
                              {protocol.labs.length} labs
                            </Badge>
                            <Badge variant="outline" className="text-[10px] border-amber-500/30 text-amber-400">
                              <Calendar className="h-3 w-3 mr-1" />
                              {protocol.followUpWeeks}wk follow-up
                            </Badge>
                          </div>

                          {/* Tags */}
                          <div className="flex flex-wrap gap-1">
                            {protocol.tags.map((tag) => (
                              <span
                                key={tag}
                                className="text-[10px] px-2 py-0.5 rounded-full bg-[hsl(var(--ih-bg-surface))]/50 text-[hsl(var(--ih-text-muted))]"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                        <ChevronRight className="h-5 w-5 text-[hsl(var(--ih-text-muted))] flex-shrink-0" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {filteredProtocols.length === 0 && (
              <div className="text-center py-12 text-[hsl(var(--ih-text-muted))]">
                <ClipboardCheck className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No protocols match your search</p>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Selected Protocol Details */}
        {selectedProtocol && (
          <div className="border-t border-[hsl(var(--ih-border))] pt-4 -mx-6 px-6">
            <div className="bg-[hsl(var(--ih-bg-elevated))] rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="h-4 w-4 text-brand-teal" />
                <span className="text-[hsl(var(--ih-text))] font-medium text-sm">{selectedProtocol.name}</span>
              </div>

              <div className="grid grid-cols-2 gap-4 text-xs">
                <div>
                  <span className="text-[hsl(var(--ih-text-muted))] block mb-1">Medications:</span>
                  <ul className="text-[hsl(var(--ih-text-secondary))] space-y-0.5">
                    {selectedProtocol.medications.map((med, i) => (
                      <li key={i}>• {med.name} {med.dose} - {med.frequency}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <span className="text-[hsl(var(--ih-text-muted))] block mb-1">Required Labs:</span>
                  <ul className="text-[hsl(var(--ih-text-secondary))] space-y-0.5">
                    {selectedProtocol.labs.map((lab, i) => (
                      <li key={i}>• {lab}</li>
                    ))}
                  </ul>
                </div>
              </div>

              {selectedProtocol.contraindications && selectedProtocol.contraindications.length > 0 && (
                <div className="mt-3 p-2 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <span className="text-red-400 text-xs font-medium">Contraindications:</span>
                  <p className="text-red-300 text-xs mt-1">
                    {selectedProtocol.contraindications.join(', ')}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-[hsl(var(--ih-border))] text-[hsl(var(--ih-text-secondary))]"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSelectProtocol}
            disabled={isSubmitting || !selectedProtocol}
            className="bg-brand-teal hover:bg-brand-teal/90 text-white"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Applying...
              </>
            ) : (
              <>
                Apply Protocol
                <ArrowRight className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TreatmentProtocolSelector;
